import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { stationTimeAt } from '@/lib/fx';
import { useSelectionStore } from '@/state/selection';
import { stations, useTourStore } from '@/state/tour';
import { HoloScreen } from './HoloScreen';

const GRID = 8;
const SPACING = 42;

function SiteGrid() {
  const size = useSelectionStore((s) => s.heroSize);
  const ref = useRef<THREE.InstancedMesh>(null);
  const matRef = useRef<THREE.MeshStandardMaterial>(null);
  const station = stations.find((s) => s.kind === 'projects')!;

  const { count, matrices } = useMemo(() => {
    const m: THREE.Matrix4[] = [];
    const tmp = new THREE.Object3D();
    for (let i = 0; i < GRID; i++)
      for (let j = 0; j < GRID; j++) {
        const x = (i - (GRID - 1) / 2) * SPACING;
        const z = (j - (GRID - 1) / 2) * SPACING;
        if (Math.abs(x) < SPACING * 0.6 && Math.abs(z) < SPACING * 0.6) continue;
        // Deterministic variety: rotate some, scale a little.
        const seed = i * 31 + j * 17;
        tmp.position.set(x, 0, z);
        tmp.rotation.set(0, seed % 4 === 0 ? Math.PI / 2 : 0, 0);
        const s = 0.85 + ((seed * 7) % 10) / 30;
        tmp.scale.set(s, 0.9 + ((seed * 3) % 7) / 20, s);
        tmp.updateMatrix();
        m.push(tmp.matrix.clone());
      }
    return { count: m.length, matrices: m };
  }, []);

  useFrame(() => {
    const mesh = ref.current;
    if (!mesh) return;
    if (mesh.userData.filled !== count) {
      matrices.forEach((mat, i) => mesh.setMatrixAt(i, mat));
      mesh.instanceMatrix.needsUpdate = true;
      mesh.userData.filled = count;
    }
    const t = stationTimeAt(station, useTourStore.getState().progress);
    const k = Math.min(1, Math.max(0, (t - 0.25) / 0.5));
    if (matRef.current) matRef.current.opacity = 0.55 * k;
    mesh.visible = k > 0.001;
  });

  const [w, h, d] = size;
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, count]} position={[0, h / 2, 0]}>
      <boxGeometry args={[w, h, d]} />
      <meshStandardMaterial
        ref={matRef}
        color="#1a1f28"
        emissive="#f0b429"
        emissiveIntensity={0.08}
        transparent
        opacity={0}
        roughness={0.9}
      />
    </instancedMesh>
  );
}

export function ProjectsScene() {
  return (
    <>
      <SiteGrid />
      <HoloScreen position={[-36, 0, -44]} flat />
    </>
  );
}
